/**
 * URL-addressable modal state.
 *
 * Every dialog/drawer/sheet in the app mirrors its open state into a query
 * parameter so any modal can be deep-linked and shared:
 *
 *   ?modal=cart                 — cart drawer (any page)
 *   ?modal=notifications        — notifications sheet (any page with header)
 *   ?modal=menu                 — nav menu sheet (any page with header)
 *   /?legal=terms|privacy       — terms / privacy dialog on the landing page
 *   /?tier=plus&billing=yearly  — subscribe-hero checkout on the homepage
 *   /membership?tier=...        — membership checkout dialog
 *   /account?modal=cancel-membership — cancel subscription flow
 *   ?modal=super-sub-telegram     — Super Sub Telegram connect walkthrough
 *
 * The hook reads/writes the *raw* search string (not the route's validated
 * search), so it works on every route regardless of its `validateSearch`
 * schema and never drops params a schema doesn't know about.
 *
 * History semantics: setting a value pushes a new entry (back button closes
 * the modal), clearing it replaces the current entry (back button leaves the
 * page, not the closed-modal state).
 */
import { useCallback } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";

function parseSearch(searchStr: string | undefined): URLSearchParams {
  const raw = searchStr ?? "";
  return new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
}

export function useUrlParam(
  key: string,
): [
  string | undefined,
  (next: string | undefined, opts?: { replace?: boolean }) => void,
] {
  const router = useRouter();

  const value = useRouterState({
    select: (s) => parseSearch(s.location.searchStr).get(key) ?? undefined,
  });

  const setValue = useCallback(
    (next: string | undefined, opts?: { replace?: boolean }) => {
      // Read from history (not router state): history updates synchronously,
      // so two setters called back-to-back compose instead of clobbering.
      const loc = router.history.location;
      const params = parseSearch(loc.search);
      if (params.get(key) === (next ?? null) && next !== undefined) return;
      if (next === undefined) {
        if (!params.has(key)) return;
        params.delete(key);
      } else {
        params.set(key, next);
      }
      const qs = params.toString();
      const href = `${loc.pathname}${qs ? `?${qs}` : ""}${loc.hash ? `#${loc.hash}` : ""}`;
      // Clearing defaults to replace so the back button doesn't reopen the modal.
      const replace = opts?.replace ?? next === undefined;
      if (replace) {
        router.history.replace(href);
      } else {
        router.history.push(href);
      }
    },
    [router, key],
  );

  return [value, setValue];
}

/** Convenience wrapper for the shared `?modal=` parameter. */
export function useModalParam(
  name: string,
): [boolean, (open: boolean) => void] {
  const [value, setValue] = useUrlParam("modal");
  const isOpen = value === name;
  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        setValue(name);
      } else if (value === name) {
        setValue(undefined);
      }
    },
    [name, value, setValue],
  );
  return [isOpen, setOpen];
}
